import { Value } from 'classnames';
import { ProductAdjustmentTypeEnum } from 'common/constants/enum';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import productAdjustmentTypeOptions from 'components/elements/table/editable-columns/product-adjustment-type-option';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import useGetProductVariantColumn from 'hooks/use-get-product-variant-column';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { onAddNewProductVariant } from '../../hooks/use-get-default-values';
import {
  ProductAdjustmentFormType,
  ProductAdjustmentItemFormType,
} from '../form-type';

function useGetProductVariantTableList() {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const productVariantColumn = useGetProductVariantColumn(false);
  return [
    {
      header: t('common:product'),
      accessorKey: 'productVariantId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: productVariantColumn,
    },
    {
      header: t('common:type'),
      accessorKey: 'type',
      minSize: 100,
      dataType: ColumnDataType.select,
      minNumber: 0,
      required: true,
      options: productAdjustmentTypeOptions(t),
    },
    {
      header: t('common:note'),
      accessorKey: 'description',
      minSize: 200,
      dataType: ColumnDataType.text,
      minNumber: 0,
    },
    {
      header: t('common:quantity'),
      accessorKey: 'qty',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
      required: true,
    },
    {
      header: t('common:total_price'),
      accessorKey: 'totalPrice',
      minSize: 100,
      dataType: ColumnDataType.number,
      disabled: true,
    },

    ...deleteColumn(editable),
  ];
}

export default function ProductVariantTable() {
  const { editable } = useFormState();
  const columns = useGetProductVariantTableList();
  const { control, trigger, getValues, clearErrors } =
    useFormContext<ProductAdjustmentFormType>();

  const name = 'items' as const;

  const { fields, update, remove, append } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof ProductAdjustmentItemFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;
      const currentRowIndex = action.rowIndex as number;
      const items = getValues(name);
      const item = items[currentRowIndex];

      const otherVal: () => Partial<ProductAdjustmentItemFormType> = () => {
        const isOut = (val) => {
          return val === ProductAdjustmentTypeEnum.Out;
        };

        if (columnId === 'productVariantId') {
          const price = isOut(item.type)
            ? undefined
            : extra?.[0]?.extra?.sellPrice;

          const totalPrice = (item.qty || 0) * (price || 0);

          return {
            productName: extra?.[0]?.label,
            price,
            stock: extra?.[0]?.extra?.stock,
            tempPrice: extra?.[0]?.extra?.sellPrice,
            totalPrice: totalPrice || undefined,
          };
        }

        if (columnId === 'qty') {
          return {
            totalPrice: value * (item.price || 0) || undefined,
          };
        }

        if (columnId === 'price') {
          return {
            totalPrice: value * (item.qty || 0),
          };
        }

        if (columnId === 'type') {
          const price = isOut(value) ? undefined : item.tempPrice;
          const totalPrice = (item.qty || 0) * (price || 0);

          return {
            price,
            totalPrice: totalPrice || undefined,
          };
        }

        return {};
      };

      if (type === 'addRow') {
        append(onAddNewProductVariant());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...item,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <SeparatorVertical $margin={36} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ rowOriginal, accessorKey }) => {
          return rowOriginal.type === 'out' && accessorKey === 'price';
        }}
      />
    </div>
  );
}
